import { Icon } from '@iconify/react';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  TextField,
} from '@mui/material';
import { useMemo, useState } from 'react';
import type {
  TopvisorCatalog,
  TopvisorCatalogProject,
  TopvisorCatalogRegion,
  TopvisorReport,
} from '../api/types';
import { Box, Grid, Stack, Typography } from './mui-system';

export interface TopvisorCatalogSelection {
  projectId: number;
  regionIndex: number;
  positions: boolean;
  serp: boolean;
}

export function TopvisorCatalogPanel({
  catalog,
  reports,
  loading,
  busy,
  error,
  warning,
  onRefresh,
  onImport,
}: {
  catalog: TopvisorCatalog | null;
  reports: TopvisorReport[];
  loading: boolean;
  busy: boolean;
  error: string;
  warning: string;
  onRefresh(): void;
  onImport(items: TopvisorCatalogSelection[]): void;
}) {
  const [query, setQuery] = useState('');
  const [includePositions, setIncludePositions] = useState(true);
  const [includeSerp, setIncludeSerp] = useState(true);
  const [selection, setSelection] = useState<{ signature: string; keys: Set<string> }>({
    signature: '',
    keys: new Set(),
  });
  const allRegions = useMemo(() => catalogRegions(catalog), [catalog]);
  const selectionSignature = useMemo(() => [...allRegions.keys()].join('|'), [allRegions]);
  const selected =
    selection.signature === selectionSignature
      ? selection.keys
      : new Set(selectionSignature ? selectionSignature.split('|') : []);

  const visibleProjects = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!catalog || !needle) return catalog?.projects ?? [];
    return catalog.projects.filter(
      (project) =>
        project.name.toLowerCase().includes(needle) ||
        project.url?.toLowerCase().includes(needle) ||
        String(project.id).includes(needle),
    );
  }, [catalog, query]);
  const selectedRegions = [...selected]
    .map((key) => allRegions.get(key))
    .filter((item): item is { project: TopvisorCatalogProject; region: TopvisorCatalogRegion } =>
      Boolean(item),
    );
  const profileCount = selectedRegions.reduce(
    (count, { project }) =>
      count + Number(includePositions) + Number(includeSerp && project.snapshotsEnabled),
    0,
  );
  const skippedSerpCount = includeSerp
    ? selectedRegions.filter(({ project }) => !project.snapshotsEnabled).length
    : 0;
  const allSelected = allRegions.size > 0 && selected.size === allRegions.size;
  const existing = useMemo(
    () =>
      new Set(
        reports.map((report) => `${report.topvisorProjectId}:${report.regionIndex}:${report.type}`),
      ),
    [reports],
  );

  function toggle(key: string) {
    setSelection(() => {
      const next = new Set(selected);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return { signature: selectionSignature, keys: next };
    });
  }

  function submit() {
    onImport(
      selectedRegions.map(({ project, region }) => ({
        projectId: project.id,
        regionIndex: region.index,
        positions: includePositions,
        serp: includeSerp,
      })),
    );
  }

  return (
    <Card sx={{ mb: 2.2, borderColor: 'primary.main' }}>
      <CardContent sx={{ p: { xs: 2, md: 2.7 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          gap={1.5}
          alignItems={{ md: 'center' }}
        >
          <Stack direction="row" gap={1.2} alignItems="center">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                bgcolor: 'rgba(74,126,187,.13)',
                color: 'primary.main',
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <Icon icon="solar:cloud-download-bold-duotone" width={25} />
            </Box>
            <Box>
              <Typography variant="h3">Проекты Topvisor</Typography>
              <Typography color="text.secondary" fontSize={12.5}>
                Живой каталог — названия, регионы и устройства берутся напрямую из Topvisor.
              </Typography>
            </Box>
          </Stack>
          <Button
            variant="outlined"
            disabled={loading || busy}
            onClick={onRefresh}
            startIcon={
              loading ? <CircularProgress size={15} /> : <Icon icon="solar:refresh-linear" />
            }
          >
            Обновить каталог
          </Button>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mt: 1.8 }}>
            {error}
          </Alert>
        )}
        {warning && (
          <Alert severity="warning" sx={{ mt: 1.8 }}>
            {warning}
          </Alert>
        )}
        {catalog?.warnings.map((warning) => (
          <Alert key={warning} severity="warning" sx={{ mt: 1.2 }}>
            {warning}
          </Alert>
        ))}

        {loading && !catalog ? (
          <Box py={5} textAlign="center">
            <CircularProgress />
          </Box>
        ) : catalog ? (
          <>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              gap={1.3}
              mt={2.2}
              alignItems={{ md: 'center' }}
            >
              <TextField
                size="small"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Найти проект или домен"
                sx={{ flex: 1, minWidth: 220 }}
                slotProps={{ input: { startAdornment: <Icon icon="solar:magnifer-linear" /> } }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includePositions}
                    onChange={(event) => setIncludePositions(event.target.checked)}
                  />
                }
                label="Позиции"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeSerp}
                    onChange={(event) => setIncludeSerp(event.target.checked)}
                  />
                }
                label="Снимки выдачи"
              />
              <Button
                size="small"
                onClick={() =>
                  setSelection({
                    signature: selectionSignature,
                    keys: allSelected ? new Set() : new Set([...allRegions.keys()]),
                  })
                }
              >
                {allSelected ? 'Снять выбор' : 'Выбрать все'}
              </Button>
            </Stack>

            <Box sx={{ maxHeight: 520, overflowY: 'auto', mt: 1.6, pr: { md: 0.7 } }}>
              <Grid container spacing={1.2}>
                {visibleProjects.map((project) => (
                  <Grid key={project.id} size={{ xs: 12, xl: 6 }}>
                    <CatalogProject
                      project={project}
                      selected={selected}
                      existing={existing}
                      onToggle={toggle}
                    />
                  </Grid>
                ))}
              </Grid>
              {!visibleProjects.length && (
                <Typography color="text.secondary" textAlign="center" py={4}>
                  Проекты не найдены
                </Typography>
              )}
            </Box>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ sm: 'center' }}
              gap={1.2}
              mt={2}
            >
              <Box>
                <Typography color="text.secondary" fontSize={12}>
                  Выбрано конфигураций: {selected.size}. Повторный импорт обновит существующие
                  профили без дублей и включит выбранные отключённые профили. Снятие галочки не
                  отключает отчёт — для этого откройте его и нажмите кнопку с корзиной.
                </Typography>
                {skippedSerpCount > 0 && (
                  <Typography color="warning.main" fontSize={12} mt={0.3}>
                    Снимков будет пропущено: {skippedSerpCount} — сбор выдачи выключен в Topvisor.
                  </Typography>
                )}
              </Box>
              <Button
                variant="contained"
                disabled={busy || !selected.size || !profileCount}
                onClick={submit}
                startIcon={
                  busy ? <CircularProgress size={15} /> : <Icon icon="solar:add-square-linear" />
                }
              >
                Синхронизировать {profileCount}{' '}
                {plural(profileCount, 'профиль', 'профиля', 'профилей')}
              </Button>
            </Stack>
          </>
        ) : (
          <Typography color="text.secondary" mt={2}>
            Нажмите «Обновить каталог», чтобы получить проекты из Topvisor.
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

function CatalogProject({
  project,
  selected,
  existing,
  onToggle,
}: {
  project: TopvisorCatalogProject;
  selected: Set<string>;
  existing: Set<string>;
  onToggle(key: string): void;
}) {
  return (
    <Box
      sx={{
        height: '100%',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        p: 1.6,
        opacity: project.active ? 1 : 0.62,
      }}
    >
      <Stack direction="row" justifyContent="space-between" gap={1} alignItems="flex-start">
        <Box>
          <Typography fontWeight={750}>{project.name}</Typography>
          <Typography color="text.secondary" fontSize={11.5} mt={0.2}>
            {project.url ?? `Project ID ${project.id}`}
          </Typography>
          {project.lastCheckedAt && (
            <Typography color="text.secondary" fontSize={11} mt={0.25}>
              Последняя проверка: {formatCatalogDate(project.lastCheckedAt)}
            </Typography>
          )}
        </Box>
        <Stack direction="row" gap={0.5} flexWrap="wrap" justifyContent="flex-end">
          {!project.active && <Chip size="small" color="default" label="Архив" />}
          <Chip
            size="small"
            color={project.snapshotsEnabled ? 'success' : 'default'}
            label={project.snapshotsEnabled ? 'SERP включён' : 'Без SERP'}
          />
        </Stack>
      </Stack>
      {project.warnings.map((warning) => (
        <Typography key={warning} color="warning.main" fontSize={11.5} mt={0.8}>
          {warning}
        </Typography>
      ))}
      <Stack mt={1.2} gap={0.55}>
        {project.regions.map((region) => {
          const key = selectionKey(project.id, region.index);
          return (
            <Box
              key={key}
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'center' },
                justifyContent: 'space-between',
                gap: { xs: 0, sm: 1 },
                borderRadius: 1.3,
                bgcolor: 'action.hover',
                pr: { xs: 0.7, sm: 1 },
              }}
            >
              <FormControlLabel
                sx={{ m: 0, flex: 1, minWidth: 0 }}
                control={
                  <Checkbox
                    size="small"
                    checked={selected.has(key)}
                    disabled={!project.active}
                    onChange={() => onToggle(key)}
                  />
                }
                label={
                  <Typography fontSize={12.5}>
                    {region.searcherName} · {region.name} · {deviceLabel(region.device)} · Top-
                    {region.depth}
                  </Typography>
                }
              />
              <Stack direction="row" gap={0.4} ml={{ xs: 4.6, sm: 0 }} mb={{ xs: 0.8, sm: 0 }}>
                {existing.has(`${key}:positions`) && (
                  <Chip size="small" variant="outlined" label="позиции" />
                )}
                {existing.has(`${key}:serp`) && (
                  <Chip size="small" variant="outlined" label="SERP" />
                )}
              </Stack>
            </Box>
          );
        })}
        {!project.regions.length && (
          <Typography color="text.secondary" fontSize={12}>
            В проекте нет включённых регионов проверки позиций.
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

function catalogRegions(catalog: TopvisorCatalog | null) {
  const regions = new Map<
    string,
    { project: TopvisorCatalogProject; region: TopvisorCatalogRegion }
  >();
  for (const project of catalog?.projects ?? []) {
    if (!project.active) continue;
    for (const region of project.regions) {
      regions.set(selectionKey(project.id, region.index), { project, region });
    }
  }
  return regions;
}

function selectionKey(projectId: number, regionIndex: number): string {
  return `${projectId}:${regionIndex}`;
}

function deviceLabel(device: TopvisorCatalogRegion['device']): string {
  if (device === 'mobile') return 'смартфон';
  if (device === 'tablet') return 'планшет';
  return 'ПК';
}

function plural(value: number, one: string, few: string, many: string): string {
  const mod100 = value % 100;
  const mod10 = value % 10;
  if (mod100 >= 11 && mod100 <= 14) return many;
  if (mod10 === 1) return one;
  if (mod10 >= 2 && mod10 <= 4) return few;
  return many;
}

function formatCatalogDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('ru-RU', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
}
